import { FieldType } from '../../../types/forms/validation';
import { Listing } from '../../../types/listing/listing';
import { createCoordinates, checkCoordinates } from '../../../utils/map';
import { GLFile } from '../../../types/listing/file';
import { checkFilesThatNeedProcessing } from '../../../utils/image-check';
import { Space } from '../../../types/listing/space';
import { Config } from '../../../types/config/config';
import { findSpecificationFieldsByConditions } from '../../../utils/config/specifications-fields';

export interface ManualError {
    error: boolean,
    message: string
}

export interface ManualErrorList {
    error: boolean,
    message: string[]
}

export interface PublishedExtraInfo {
    manual: object,
    errors: string[];
}

export const publishedPropertyValidations: object = {
    'propertyRecordName': {
        type: FieldType.STRING,
        required: true,
        messages: {
            'required': 'A property record name is required.'
        }
    },
    'propertyName': {
        type: FieldType.STRING,
        required: false,
        messages: {
            'required': 'A property name is required.'
        }
    },
    'listingType': {
        type: FieldType.STRING,
        required: true,
        messages: {
            'required': 'A listing type is required.'
        }
    },
    'propertyType': {
        type: FieldType.STRING,
        required: true,
        messages: {
            'required': 'A property type is required.'
        }
    },
    'street': {
        type: FieldType.STRING,
        required: true,
        length: 3,
        messages: {
            'required': 'A street address is required.',
            'length': 'A street address with at least 3 characters is required.'
        }
    },
    'city': {
        type: FieldType.STRING,
        required: true,
        messages: {
            'required': 'A city is required.'
        }
    },
    'postalCode': {
        type: FieldType.STRING,
        required: true,
        messages: {
            'required': 'A postal code is required.'
        }
    },
    'headline': {
        type: FieldType.STRING,
        required: false,
        messages: {
            'required': 'A headline is required.'
        }
    },
    'buildingDescription': {
        type: FieldType.STRING,
        required: true,
        messages: {
            'required': 'A property description is required.'
        }
    },
    'photos': {
        type: FieldType.ARRAY,
        required: true,
        messages: {
            'required': 'At least one photo is required.'
        }
    },
    'contacts': {
        type: FieldType.ARRAY,
        required: true,
        messages: {
            'required': 'At least one contact is required.'
        }
    }
}

export const extraPublishedValidationFunc = (listing: Listing, config: Config, errors: string[]): PublishedExtraInfo => {

    const isPropertyHasValue = (property: any): boolean => {
        return !!property;
    };

    const isValidAgainstOptions = (value: string, options: any[]) => {
        return Array.isArray(options) && options.find(op => op.value === value);
    }

    // provide validations outside of yup
    const manualErrorObj: object = {};
    let isOnePhotoActive = false;
    listing.photos.map((p: GLFile) => {
        if (p.active === true) { isOnePhotoActive = true }
    })
    if (!listing.photos || listing.photos.length === 0 || isOnePhotoActive === false) {
        const photos: ManualError = { error: true, message: '- At least one photo is required.' };
        const photosFieldName = 'photos';
        manualErrorObj[photosFieldName] = photos;
        errors.push("At least one photo must be included.");
    }
    if (!checkCoordinates(createCoordinates(listing))) {
        const coords: ManualError = { error: true, message: 'An address that displays on the map is required.' };
        const coordsFieldName = 'coords';
        manualErrorObj[coordsFieldName] = coords;
        errors.push("Coordinates are required.");
    }
    if (!listing.contacts || listing.contacts.length === 0) {
        const contacts: ManualError = { error: true, message: 'At least one contact is required.' };
        const contactsFieldName = 'contacts';
        manualErrorObj[contactsFieldName] = contacts;
        errors.push("At least one contact is required.");
    }

    if (config.siteId === "au-comm" && listing.contacts && listing.contacts.length > 0 && !listing.contacts.some(c => c.email.includes("cbre"))) {
        const contacts: ManualError = { error: true, message: 'At least one CBRE contact is required.' };
        const contactsFieldName = 'contacts';
        manualErrorObj[contactsFieldName] = contacts;
        errors.push("At least one contact is required.");
    }

    if (config && config.highlight && config.highlight.required && listing.highlights.length < 3) {
        const highlights: ManualError = { error: true, message: 'At least 3 highlights should be added' };
        const highlightsFieldName = 'highlights';
        manualErrorObj[highlightsFieldName] = highlights;
        errors.push("At least 3 highlights should be added");
    }

    const isSizesRequired: boolean = Boolean(config && config.sizesandmeasurements && config.sizesandmeasurements.required);

    if (isSizesRequired && listing.propertySizes && listing.propertySizes.length === 0) {
        const sizesAndMeasurements: ManualError = { error: true, message: 'At least 1 Size should be added' };
        const sizesAndMeasurementsFieldName = 'sizes';
        manualErrorObj[sizesAndMeasurementsFieldName] = sizesAndMeasurements;
        errors.push("At least 1 Size should be added");
    }

    if (config && config.validations && config.validations.manualValidations) {
        const manualValidations = config.validations && config.validations.manualValidations;
        const specifications = listing && listing.specifications;
        let messages: string[] = [];
        if(config.featureFlags.specificationsRules && config.specifications.fields) {
            const fields = findSpecificationFieldsByConditions(config, listing);
            Object.entries(fields || {}).forEach(([key, value]) => {
                const fieldProperties = value.properties;
                const specKey = fieldProperties && fieldProperties.name ? fieldProperties.name : key;
                const specValue = (listing.specifications as any)[specKey];
                
                if (value.required) {
                    if(!isPropertyHasValue(specValue) || (fieldProperties.options && !isValidAgainstOptions(specValue, fieldProperties.options))) {
                        errors.push(`${fieldProperties.label ? fieldProperties.label : specKey} is Mandatory`);
                        messages.push(`${fieldProperties.label ? fieldProperties.label : specKey} is Mandatory`);
                    }
                }
                
                if(fieldProperties && fieldProperties.minValue && isPropertyHasValue(specValue)) {
                    if(typeof specValue === 'number' && specValue < fieldProperties.minValue) {
                        if(fieldProperties.minValueMessage) {
                            errors.push(fieldProperties.minValueMessage);
                            messages.push(fieldProperties.minValueMessage);
                        } else {
                            errors.push(`${fieldProperties.label ? fieldProperties.label : specKey} must be at least ${fieldProperties.minValue}`);
                            messages.push(`${fieldProperties.label ? fieldProperties.label : specKey} must be at least ${fieldProperties.minValue}`);
                        }
                    }
                }
            });
        } else {
            if (listing && listing.listingType === "lease" || listing.listingType === "salelease") {
                if ((manualValidations && manualValidations.minPrice && manualValidations.minPrice.required) && specifications && !isPropertyHasValue(listing.specifications.minPrice)) {
                    messages.push(manualValidations.minPrice.messages.required);
                    errors.push("Min Price is Mandatory");
                }
                if ((manualValidations && manualValidations.maxPrice && manualValidations.maxPrice.required) && specifications && !isPropertyHasValue(listing.specifications.maxPrice)) {
                    messages.push(manualValidations.maxPrice.messages.required);
                    errors.push("Max Price is Mandatory");
                }
                if ((manualValidations && manualValidations.totalSpace && manualValidations.totalSpace.required) && specifications && !isPropertyHasValue(listing.specifications.totalSpace)) {
                    messages.push(manualValidations.totalSpace.messages.required);
                    errors.push("Total Space is Mandatory");
                }
                if ((manualValidations && manualValidations.measure && manualValidations.measure.required) && specifications && !isPropertyHasValue(listing.specifications.measure)) {
                    messages.push(manualValidations.measure.messages.required);
                    errors.push("Unit of Measurement is Mandatory");
                }
            }
            if (listing && listing.listingType === "sale" || listing.listingType === "salelease") {
                if ((manualValidations && manualValidations.salePrice && manualValidations.salePrice.required) && specifications && (!isPropertyHasValue(listing.specifications.salePrice)
                    || (listing.specifications.salePrice < (manualValidations && manualValidations.salePrice && manualValidations.salePrice.minValue)))) {
                    messages.push(manualValidations.salePrice.messages.required);
                    errors.push("Sale Price is Mandatory");
                }
                if ((manualValidations && manualValidations.totalSpace && manualValidations.totalSpace.required) && specifications && !isPropertyHasValue(listing.specifications.totalSpace)) {
                    messages.push(manualValidations.totalSpace.messages.required);
                    errors.push("Total Space is Mandatory");
                }
                if ((manualValidations && manualValidations.measure && manualValidations.measure.required) && specifications && !isPropertyHasValue(listing.specifications.measure)) {
                    messages.push(manualValidations.measure.messages.required);
                    errors.push("Unit of Measurement is Mandatory");
                }
            }
        }
        
        if (messages.length > 0) {
            const specificationsError: ManualErrorList = { error: true, message: messages };
            const minPriceFieldName = 'specifications';
            manualErrorObj[minPriceFieldName] = specificationsError;
        }
        if (isSizesRequired && manualValidations.sizesandmeasurements && manualValidations.sizesandmeasurements.required) {
            const isValidListingType = Boolean(Array.isArray(manualValidations.sizesandmeasurements.listingType) && manualValidations.sizesandmeasurements.listingType.includes(listing.listingType));
            if (listing.propertySizes && listing.propertySizes.length > 0 && isValidListingType) {
                const sizesErrors: string[] = [];
                const isUnitofMeasureRequired = Boolean(manualValidations.sizesandmeasurements.unitofmeasure && manualValidations.sizesandmeasurements.unitofmeasure.required);
                let hasInvalidUom = true;
                listing.propertySizes.forEach((size: any) => {
                    if(isUnitofMeasureRequired && !isPropertyHasValue(size.measureUnit) && hasInvalidUom) {
                        sizesErrors.push(manualValidations.sizesandmeasurements.unitofmeasure.messages.required);
                        errors.push("Unit of Measure is Mandatory");
                        hasInvalidUom = false;
                    }
                    return hasInvalidUom;
                });
                if (sizesErrors.length > 0) {
                    const sizesFieldsError: ManualErrorList = { error: true, message: sizesErrors };
                    const sizesFieldName = 'sizes';
                    manualErrorObj[sizesFieldName] = sizesFieldsError;
                }
            }
            
            // Check sizeType validation for Land/Development property type
            const isSizeTypeRequired = Boolean(manualValidations.sizesandmeasurements.sizeType && manualValidations.sizesandmeasurements.sizeType.required);
            if (isSizeTypeRequired && manualValidations.sizesandmeasurements.sizeType.propertyType.includes(listing.propertyType)) {
                const sizesErrors: string[] = [];
                const requiredSizeKinds = manualValidations.sizesandmeasurements.sizeType.sizeKind;
                const hasSizeKind = listing.propertySizes && listing.propertySizes.some((size: any) => requiredSizeKinds.includes(size.sizeKind));
                if (!hasSizeKind) {
                    sizesErrors.push(manualValidations.sizesandmeasurements.sizeType.messages.required);
                    errors.push(manualValidations.sizesandmeasurements.sizeType.messages.required);
                    const sizesFieldsError: ManualErrorList = { error: true, message: sizesErrors };
                    const sizesFieldName = 'sizes';
                    manualErrorObj[sizesFieldName] = sizesFieldsError;
                }
            }
        }
        const isLeasedInfoRequired = Boolean(manualValidations.leasedInfo && manualValidations.leasedInfo.required && config.leasedInfo && config.leasedInfo.show && listing.listingType === "leased");
        if (isLeasedInfoRequired) {
            messages = [];
            if (!isPropertyHasValue(listing.leasedInfo.rentPerAnnum)) {
                messages.push(manualValidations.leasedInfo.rentPerAnnum.messages.required);
                errors.push("Rent Per Annum is Mandatory");
            }
            if (!isPropertyHasValue(listing.leasedInfo.leasedPrice)) {
                messages.push(manualValidations.leasedInfo.leasedPrice.messages.required);
                errors.push("Leased Price is Mandatory");
            }
            if (!isPropertyHasValue(listing.leasedInfo.leasedDuration)) {
                messages.push(manualValidations.leasedInfo.leasedDuration.messages.required);
                errors.push("Leased Duration is Mandatory");
            }
            if (!isPropertyHasValue(listing.leasedInfo.leasedDate)) {
                messages.push(manualValidations.leasedInfo.leasedDate.messages.required);
                errors.push("Leased Date is Mandatory");
            }
            if (messages.length > 0) {
                const leasedInfoError: ManualErrorList = { error: true, message: messages };
                const leasedInfoFieldName = 'leasedInfo';
                manualErrorObj[leasedInfoFieldName] = leasedInfoError;
            }
        }
        const isSoldInfoRequired = Boolean(manualValidations.soldInfo && manualValidations.soldInfo.required && config.soldInfo && config.soldInfo.show && listing.listingType === "sold");
        if (isSoldInfoRequired) {
            messages = [];
            if (!isPropertyHasValue(listing.soldInfo.soldPrice)) {
                messages.push(manualValidations.soldInfo.soldPrice.messages.required);
                errors.push("Sold Price is Mandatory");
            }
            if (!isPropertyHasValue(listing.soldInfo.soldDate)) {
                messages.push(manualValidations.soldInfo.soldDate.messages.required);
                errors.push("Sold Date is Mandatory");
            }
            if (messages.length > 0) {
                const soldInfoError: ManualErrorList = { error: true, message: messages };
                const soldInfoFieldName = 'soldInfo';
                manualErrorObj[soldInfoFieldName] = soldInfoError;
            }
        }
    }


    // if watermark detection is enabled in the config, we need to ensure all images have been processed.
    if (config.watermarkDetectionFeatureFlag === true) {

        if (listing.photos && checkFilesThatNeedProcessing([], listing.photos).length > 0) {
            const photos: ManualError = { error: true, message: '- Publishing cannot complete until watermark detection is resolved. If images have been flagged for watermarks, please either remove them from your listing, select override if you have permission to use the photo, or reach out to the Global Listings team for help.' };
            const photosFieldName = 'photos';
            manualErrorObj[photosFieldName] = photos;
            errors.push("One or more photos under specifications cannot be published due to watermark detection (processing or errors).");
        }
        // floorplans  ::: NOTE: Floorplans have been disabled for watermarking, but we may need to make this dynamic if we decide to include them again.
        /*
        if(listing.floorplans && checkFilesThatNeedProcessing([],listing.floorplans).length > 0){
            const floorplans:ManualError = { error: true, message: '- Publishing cannot complete until watermark detection is resolved. If images have been flagged for watermarks, please either remove them from your listing, select override if you have permission to use the photo, or reach out to the Global Listings team for help.'}; 
            const floorplansFieldName = 'floorplans';
            manualErrorObj[floorplansFieldName] = floorplans;
            errors.push("One or more floorplans under specifications cannot be published due to watermark detection (processing or errors).");
        }
        */

        // spaces
        let spaceErrorCount: number = 0;
        if (listing.spaces && listing.spaces.length > 0) {
            listing.spaces.forEach((space: Space) => {
                if (space.photos && space.photos.length > 0) {
                    const photoProcessing: number[] = checkFilesThatNeedProcessing([], space.photos);
                    spaceErrorCount += photoProcessing.length;
                }
                /*
                if(space.floorplans && space.floorplans.length > 0){
                    const fpProcessing:number[] = checkFilesThatNeedProcessing([],space.floorplans);
                    spaceErrorCount += fpProcessing.length;
                }
                */
            });
        }
        if (spaceErrorCount > 0) {
            const spaceErrors: ManualError = { error: true, message: '- Publishing cannot complete until watermark detection is resolved.  One ore more spaces below contains an image that has been flagged for watermarks. Please either remove them from your listing, select override if you have permission to use the photo, or reach out to the Global Listings team for help.' };
            const spacesFieldName = 'spaces';
            manualErrorObj[spacesFieldName] = spaceErrors;
            errors.push("One or more spaces have photos that cannot be published due to watermark detection (processing or errors).");
        }
    }

    return {
        'manual': manualErrorObj,
        'errors': errors
    };
}